import { z } from "zod";
import { agentAttachTargetEntityTypeNameSchema, agentAttachTargetEntityTypeNames, attachableEntityTypeNameSchema, attachableEntityTypeNames } from "./entityRegistry.js";
/** Every entity type that may own rows in the `attachments` table.
 *
 * DERIVED from the entity capability registry (#1049) rather than written out
 * here: `attachments.entity_type` is a plain text column validated at runtime
 * against this list, and it used to be one of six such lists that could
 * silently disagree. Adding a type is now a `"attachable"` capability in
 * `entityRegistry.ts` plus a descriptor in `services/attachableEntities.ts` —
 * and boot fails loudly if you do only one of the two.
 *
 * This module stays the import boundary for web code
 * (`@household/shared/attachments`), so nothing about that contract changes. */
export const attachableEntityTypes = attachableEntityTypeNames;
export const attachableEntityTypeSchema = attachableEntityTypeNameSchema;
/**
 * The initial safe subset of attachable entity types the assistant's
 * `attachments.attach` write-tool (#600 Phase 2) may target. Deliberately a
 * SUBSET of attachableEntityTypes, not the whole registry:
 *
 * - Everyday, user-recognizable targets a member would name in chat ("attach
 *   this receipt to the dishwasher", "add this photo to the kitchen-remodel
 *   project", "attach this to the kitchen faucet task").
 * - Household-visible on every serving path, so the amendable approval card can
 *   safely preview the staged image to the reviewer. Finance-gated types
 *   (finance_transaction, recurring_item), ingestion-owned document_blob, and
 *   credentials-adjacent network/wifi types are EXCLUDED until the approval
 *   preview handles per-type sensitivity (open decision #3 on #600) — the
 *   canServeAttachment gate would otherwise let the agent stage a
 *   finance/private preview for a reviewer without access.
 *
 * project is defaultSensitivity "private" but household-visible via its
 * canServe rule (canAccessProjectAttachment), and proposal review is
 * owner-only, so it stays in the safe set.
 */
export const agentAttachTargetEntityTypes = agentAttachTargetEntityTypeNames;
export const agentAttachTargetEntityTypeSchema = agentAttachTargetEntityTypeNameSchema;
/**
 * Attachment file version chains (#305).
 *
 * Web code must import these via the subpath export
 * `@household/shared/attachments` — never the root barrel, which drags
 * csv-parse/Buffer into the browser bundle.
 *
 * Version semantics mirror markdown document revisions (#300): version N is
 * the state AFTER the Nth save and the `attachments` head row always mirrors
 * the latest version. 'create' marks a snapshot of an attachment's original
 * (pre-versioning) state — written lazily on the first versioned re-upload of
 * a legacy head, or by the #308 backfill.
 */
export const attachmentVersionChangeTypeSchema = z.enum(["create", "update", "revert"]);
/** One history entry from GET /attachments/:id/versions. File bodies serve
 * from the per-version URLs (previewUrl/thumbnailUrl are '' when
 * unavailable, matching the head attachment DTO convention). */
export const attachmentVersionSchema = z.object({
    versionNumber: z.number().int().positive(),
    changeType: attachmentVersionChangeTypeSchema,
    changeNote: z.string(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string(),
    originalName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    fileUrl: z.string(),
    previewUrl: z.string(),
    thumbnailUrl: z.string()
});
/** Synthesized "current" entry: always present so the UI never special-cases
 * heads with an empty history (#308 backfill pending). versionNumber is the
 * latest version number, or null for a legacy head with no version rows yet;
 * its URLs are the HEAD serving routes, so it always resolves to the current
 * file. */
export const attachmentCurrentVersionSchema = z.object({
    versionNumber: z.number().int().positive().nullable(),
    changeNote: z.string(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string(),
    originalName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    fileUrl: z.string(),
    previewUrl: z.string(),
    thumbnailUrl: z.string()
});
/** Response of GET /attachments/:id/versions — also returned by the
 * POST /versions (upload-as-version) and POST /restore mutations, so clients
 * always hold a fresh history after a mutation. `versions` is newest-first
 * and empty for legacy heads that were never re-uploaded. */
export const attachmentVersionListResponseSchema = z.object({
    attachmentId: z.number().int().positive(),
    current: attachmentCurrentVersionSchema,
    versions: z.array(attachmentVersionSchema)
});
/** Multipart field accepted alongside the file on POST /attachments/:id/versions. */
export const attachmentVersionChangeNoteSchema = z.string().trim().max(500);
/** Body of POST /attachments/:id/restore. */
export const restoreAttachmentVersionSchema = z.object({
    toVersion: z.number().int().positive(),
    changeNote: attachmentVersionChangeNoteSchema.optional()
});
// ---------------------------------------------------------------------------
// What an attachment IS: the collection-type vocabulary (#1174).
// ---------------------------------------------------------------------------
/**
 * The kinds of DOCUMENT a household attaches to a thing (#1174).
 *
 * Before this epic the column said `'file' | 'manual_page'` — "manual pages,
 * or literally anything else". A warranty and a photo of the serial plate were
 * the same value, so nothing downstream could tell a manual worth extracting
 * text from apart from a snapshot of the box it came in. The owner's Run prep
 * rev 2 decision was to get rid of the legacy field rather than add a second
 * one beside it: `'file'` is GONE, and every row that carried it is `'other'`.
 *
 * `other` is therefore the successor of `'file'` in every sense — it is the
 * column default, it is what an upload that says nothing about itself becomes,
 * and it is what the migration wrote over every pre-existing row. Code that
 * used to ask `collectionType === 'file'` asks for `'other'` now and means the
 * same thing by it.
 *
 * These are values, not labels. The words a person reads come from the i18n
 * surface keyed on them (#1176); nothing renders these strings raw.
 */
export const documentCollectionTypes = ["manual", "warranty", "receipt", "spec_sheet", "other"];
/**
 * Every value `attachments.collection_type` may hold.
 *
 * `manual_page` keeps its exact pre-#1174 meaning — one photographed page of a
 * paper manual, ordered inside a `collection_id` — and is deliberately NOT a
 * document kind: those rows are images in a collection, not a document file
 * with text to extract. It sits in this list and not in
 * `documentCollectionTypes` for that reason.
 */
export const attachmentCollectionTypes = [...documentCollectionTypes, "manual_page"];
export const attachmentCollectionTypeSchema = z.enum(attachmentCollectionTypes);
/** What an upload that declares no kind becomes, in the DB default and in
 * every schema default that mirrors it. */
export const defaultAttachmentCollectionType = "other";
/** The retired value. Kept as a named constant ONLY so the places that must
 * still RECOGNIZE it — a transfer bundle exported by an older instance — can
 * name it instead of spelling a magic string. Nothing writes it. */
export const legacyFileCollectionType = "file";
export function isDocumentCollectionType(value) {
    return typeof value === "string" && documentCollectionTypes.includes(value);
}
export function isAttachmentCollectionType(value) {
    return typeof value === "string" && attachmentCollectionTypes.includes(value);
}
/**
 * Read a collection type off an EXTERNAL wire — a transfer bundle, a restored
 * backup — where `'file'` may still be spelled by an instance that has not
 * migrated yet.
 *
 * The coercion lives here, at the one boundary that can meet the old word, so
 * that "no code path writes 'file'" stays literally true everywhere else: what
 * comes back is always a current value. Anything unrecognized is `null` — the
 * caller decides between rejecting and defaulting, because a bundle wants a
 * 400 and a repair script wants `other`.
 */
export function collectionTypeFromWire(value) {
    if (value === legacyFileCollectionType)
        return defaultAttachmentCollectionType;
    return isAttachmentCollectionType(value) ? value : null;
}
// ---------------------------------------------------------------------------
// Extracted document text: the per-page store's read contract (#1174).
// ---------------------------------------------------------------------------
/** Which lane produced a page's text (#1173 implements them; the column and
 * this vocabulary exist from #1174 so the store can record provenance from the
 * first row it ever holds). */
export const extractionMethods = ["text_layer", "ocr", "plaintext"];
export const extractionMethodSchema = z.enum(extractionMethods);
/** The hard ceiling on ONE stored page's text. A page that hits it is stored
 * truncated with `textTruncated` true, never silently shortened: a capped list
 * that does not say it was capped reads as complete, which is the whole reason
 * the flag is a column and not a runtime guess. */
export const extractedPageTextMaxChars = 20_000;
/** How many pages one read may return. The default is small because the
 * caller that wants "the next few pages" is the common one; the ceiling exists
 * so no request can ask for a whole manual in one response (#764). */
export const extractedPagesDefaultLimit = 5;
export const extractedPagesMaxLimit = 20;
/** The furthest page of results a caller may ask for. It exists because `page`
 * arrives off a query string and becomes a database OFFSET: without a ceiling,
 * `page=1e100` is a 500 rather than an empty answer. No document has 200,000
 * pages, so this costs nothing a real caller wanted. */
export const extractedPagesMaxPage = 10_000;
export const attachmentExtractedPageSchema = z.object({
    pageNumber: z.number().int().positive(),
    text: z.string(),
    /** True when the page's text hit `extractedPageTextMaxChars` at write time
     * and what is stored is a prefix of what the document holds. */
    textTruncated: z.boolean(),
    charCount: z.number().int().nonnegative(),
    extractionMethod: extractionMethodSchema,
    extractedAt: z.string()
});
/**
 * Response of GET /attachments/:id/extracted-pages.
 *
 * Bounded by construction: `pages` never exceeds `extractedPagesMaxLimit`, and
 * `totalPages` states how many exist so a caller can always tell a short page
 * of results from the end of the document. `hasMore` says the same thing
 * again in the form the caller acts on.
 *
 * `contentHash` and `extractorVersion` are the provenance of what is being
 * served, not decoration: extracted text is a declared exception to deriving
 * at read time (#1138), legitimate only while it can say which bytes and which
 * extractor it came from. Rows belonging to a superseded content hash are
 * never served at all — see `attachmentExtractedPagesEmpty`.
 */
export const attachmentExtractedPagesResponseSchema = z.object({
    attachmentId: z.number().int().positive(),
    /** The attachment's CURRENT content hash — the only one this response can
     * describe. '' for a legacy row whose file was already missing at backfill
     * time (#304), which can never carry extracted text. */
    contentHash: z.string(),
    extractorVersion: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    hasMore: z.boolean(),
    pages: z.array(attachmentExtractedPageSchema)
});
// ---------------------------------------------------------------------------
// What the extraction pipeline DID with a document (#1173).
// ---------------------------------------------------------------------------
/**
 * The outcome the extraction queue records for one document's bytes.
 *
 * These are states of the READING, which is a different question from "is
 * there text" — and the pipeline needs both, because a queue that drives off
 * page-presence alone re-reads a genuinely blank document forever
 * (`needsExtraction` says exactly this). A page of a photograph and a page of
 * a manual are both "no rows stored"; only one of them is worth reading again.
 *
 * - `extracted` — every page that could yield text did. Nothing is pending.
 * - `partial` — some pages have text and some do not YET, because the OCR lane
 *   could not run for them (the vision host was unreachable or busy). The
 *   readable half is stored and searchable meanwhile; the document stays on
 *   the work list until its attempts run out.
 * - `unextractable` — the document was read successfully and genuinely holds
 *   no text: a page of pure vector artwork, a scan the OCR lane came back
 *   empty on. Terminal for these bytes, and NOT a failure — the honest answer
 *   to "what does this say" is "nothing".
 * - `failed` — the document could not be read at all (corrupt file, missing
 *   blob). Terminal once attempts are exhausted, and always retryable by an
 *   operator.
 */
export const documentExtractionStatuses = ["extracted", "partial", "unextractable", "failed"];
export const documentExtractionStatusSchema = z.enum(documentExtractionStatuses);
/** How much of a failure message is worth keeping on the attachment. Long
 * enough to name the cause, short enough that a wedged host cannot grow the
 * metadata column one stack trace at a time. */
export const documentExtractionErrorMaxChars = 300;
/**
 * The extraction state block stored at `attachments.metadata.extraction`.
 *
 * ONE key, not the two that `metadata.vision` + `metadata.visionError` use.
 * The image pipeline's pair has to remember to delete the error half on
 * success, and two records of one fact that must be kept in agreement is the
 * shape this epic exists to stop repeating (#1138). Here the record simply
 * says what happened, including that it failed and how many times.
 *
 * `contentHash` is what makes the block trustworthy rather than merely
 * present: it states which bytes were read. A re-upload moves the attachment
 * head, and this block instantly describes a file that is no longer attached —
 * so every reader compares it against the head's CURRENT hash and treats a
 * mismatch as "not read yet", exactly as the per-page store does. That is also
 * what re-queues a replaced document without a sweep, and what stops a
 * permanently-failed document from staying failed forever after someone
 * uploads a fixed copy over it.
 *
 * The text itself is NOT here and never will be: it lives in
 * `attachment_extracted_pages`, which is the one home for it.
 */
export const attachmentExtractionStateSchema = z.object({
    status: documentExtractionStatusSchema,
    /** The `content_hash` of the bytes this state describes. */
    contentHash: z.string(),
    /** The extractor generation that produced it; a bump re-reads the corpus. */
    extractorVersion: z.number().int().nonnegative(),
    /** Pages whose text reached the store for these bytes. */
    pageCount: z.number().int().nonnegative(),
    /** Pages read but not yet turned into text — the OCR lane could not run for
     * them. Non-zero exactly when `status` is `partial`, and the reason a
     * partial document is honest rather than silently short. */
    pendingPages: z.number().int().nonnegative(),
    /** Which lanes produced this document's text, in the order first used. */
    methods: z.array(extractionMethodSchema),
    /** Reads attempted against these bytes. Bounds retries; an operator reset
     * puts it back to zero. */
    attempts: z.number().int().nonnegative(),
    /** Why the last attempt did not complete. Present on `partial` and `failed`,
     * absent otherwise. */
    lastError: z.string().max(documentExtractionErrorMaxChars).optional(),
    /**
     * The earliest the queue will look at this document again, when the last
     * attempt stopped for a reason that was not about the document.
     *
     * This is what separates "the file is broken" from "the world is". A scan
     * waiting on an unreachable vision host, or a file whose blob is missing
     * from disk, has nothing wrong with it that a retry cannot fix — so those
     * attempts are NOT counted, and the document comes back on its own when the
     * time passes. Counting them instead spends a three-attempt budget in about
     * three seconds and then excludes the document permanently, so bringing the
     * host back would fix nothing.
     *
     * Absent when the last attempt was about the document itself (a corrupt
     * PDF), which is what `attempts` is for.
     */
    retryAfter: z.string().optional(),
    /**
     * The 1-based page the NEXT pass over these bytes starts at (#1430).
     *
     * A read stops when the document runs past its page or time budget, and
     * before this it stopped without remembering where: every retry — a member's
     * button, the queue re-picking the row, `retry-failed` — restarted at page
     * one and stopped in the same place, so a document longer than the budget
     * could never be read past it and every retry re-did work already stored.
     *
     * It is a fact about THESE bytes and nothing else. `contentHash` above is
     * what makes that safe: an offset measured on a file the attachment no
     * longer has must never be applied to the file it has now, which would leave
     * a page set that is half one document and half another. Every reader asks
     * `isCurrentExtractionState` first, and the queue's SQL asks the same
     * question, so a re-upload retires this the same way it retires `attempts`.
     *
     * Absent when there is nothing to resume to: a document read to the end, or
     * one whose last pass failed outright.
     */
    resumeFromPage: z.number().int().positive().optional(),
    /**
     * How many pages BEFORE `resumeFromPage` are still owed.
     *
     * A pass can reach a page and fail to read it — a damaged page, a scan this
     * extractor cannot decode — and a resume starts after such a page rather
     * than going back for it. Those pages are owed all the same, so the count
     * travels forward and `pendingPages` stays the real number outstanding
     * rather than only what the LAST pass could not reach.
     *
     * Paired with `resumeFromPage`: present exactly when it is.
     */
    pendingBeforeResume: z.number().int().nonnegative().optional(),
    /**
     * How many passes over these bytes have been made, including the one that
     * wrote this block (#1430).
     *
     * A pass that gets further than the last one costs no attempt, which is what
     * lets a document longer than one pass's page budget be finished at all —
     * and which means the attempt count no longer bounds how long one uploaded
     * file may occupy the single shared worker. This is the bound that replaces
     * it: past `MAX_RESUME_PASSES` a pass still runs and still keeps what it
     * read, but it spends an attempt like any other, so the document stops
     * within a stated number of budgets rather than within however many pages
     * the file claims to have.
     *
     * Paired with `resumeFromPage` like the count above: present exactly when
     * there is a next pass to bound, and retired by a re-upload along with the
     * rest of the block.
     */
    passes: z.number().int().nonnegative().optional(),
    updatedAt: z.string()
});
/**
 * Is this state about the attachment's CURRENT file, read by the CURRENT
 * extractor?
 *
 * The one question every reader of the block has to ask first. A state that
 * fails it is not stale data to be tidied up — it describes a document nobody
 * has any more, and treating it as an answer is how a screen comes to say
 * "extracted" about a file whose text it cannot show.
 */
export function isCurrentExtractionState(state, contentHash, extractorVersion) {
    return Boolean(state) && state.contentHash === contentHash && state.extractorVersion === extractorVersion;
}
/**
 * Why the extraction queue's last attempt did not finish, as a code rather
 * than a sentence (#1461).
 *
 * Settings → Background jobs renders that fault as its "last problem" line,
 * and the API used to hand it an English literal — so the whole panel read in
 * Spanish except the one line that said what had gone wrong. The standing rule
 * is that the API returns keys and data and the client renders the words; this
 * vocabulary is what lets this queue keep it. The worker names WHICH fault, the
 * client owns the sentence in `en` and `es`.
 *
 * - `blob_missing` — the attachment's file is not on disk. A fact about the
 *   DISK rather than the document, which is why it defers instead of spending
 *   one of the file's attempts.
 * - `pages_unread` — the document was read, but some pages have no text yet
 *   because the OCR lane could not run for them.
 * - `document_unreadable` — the file could not be opened as the kind of
 *   document it claims to be: a corrupt PDF, a truncated upload.
 * - `extractor_error` — anything else the read threw. A bug in a lane, a
 *   database that went away mid-read; only the raw detail describes it.
 *
 * Here and not in `schemas.ts` for the same reason as
 * `documentExtractionQueueActionSchema` below: that file is one of the four
 * agent-tool fingerprint sources, and this queue's ops vocabulary has nothing
 * to do with the assistant.
 */
export const documentExtractionFaultCodes = [
    "blob_missing",
    "pages_unread",
    "document_unreadable",
    "extractor_error"
];
export const documentExtractionFaultCodeSchema = z.enum(documentExtractionFaultCodes);
/**
 * Does this value name a fault this build knows how to word?
 *
 * The guard, and not a cast, because the value arrives over the wire: an API
 * one release ahead of the bundle reading it can name a code that has no
 * sentence here yet, and a client that assumed otherwise would paint the raw
 * code at an operator. A `false` here is what sends the caller to the fault's
 * English `message` instead, which is always populated.
 */
export function isDocumentExtractionFaultCode(value) {
    return typeof value === "string" && documentExtractionFaultCodes.includes(value);
}
/**
 * Body of `POST /api/v1/attachments/document-extraction-queue`.
 *
 * Here rather than in `schemas.ts` on purpose. That file is one of the four
 * sources of the agent-tool fingerprint
 * (`apps/api/scripts/agent-tool-source-fingerprint.mjs`), so adding to it from
 * a milestone-A item would re-trigger the finetuning catalog freshness gate for
 * a change that has nothing to do with the assistant. This module is already
 * the shared home for everything about attachments and is not a fingerprint
 * source.
 *
 * The gate is no longer a reason to place code anywhere in particular (#1129):
 * it grades a fingerprint miss as "only the stamp moved" versus "a tool contract
 * moved", and `main` refreshes the stamp itself. The placement above is still
 * right on its own merits - attachments belong with attachments - so it stays,
 * but a future schema should be sited where it belongs rather than where the
 * gate is quiet.
 */
export const documentExtractionQueueActionSchema = z.object({
    // `retry-failed` (#1179) hands a fresh attempt budget to the documents that
    // used theirs up, which is the one piece of queue ops that cannot be
    // expressed as pause/resume: an exhausted document is invisible to the
    // picker forever otherwise, and the alternative — raising MAX_ATTEMPTS —
    // spends the budget of every healthy document to rescue a handful.
    action: z.enum(["pause", "resume", "retry-failed"])
});
// ---------------------------------------------------------------------------
// What a SCREEN is told about extraction (#1176).
// ---------------------------------------------------------------------------
/**
 * The extraction states a document row can be rendered in.
 *
 * `queued` is the one that is not in `documentExtractionStatuses`, and it is
 * not a fifth stored state: it is the absence of a current one. A document the
 * queue has not reached yet, and a document whose file was just replaced, both
 * have nothing true to say about the bytes on the attachment now — and both are
 * about to be read. Saying "queued" for that is the honest rendering; saying
 * nothing leaves a row that looks finished, and reusing `failed` accuses a
 * document that was never read of being broken.
 *
 * The other four are `documentExtractionStatuses` unchanged, so a status word
 * means the same thing on the screen as it does in the state block.
 */
export const attachmentExtractionViewStatuses = ["queued", ...documentExtractionStatuses];
export const attachmentExtractionViewStatusSchema = z.enum(attachmentExtractionViewStatuses);
/**
 * The extraction block on the attachment DTO.
 *
 * Deliberately much smaller than `attachmentExtractionStateSchema`: a screen
 * needs to say what happened and offer a retry, so it gets the status, the page
 * counts, and one boolean. `attempts`, `retryAfter`, `contentHash` and
 * `extractorVersion` stay server-side — they are how the queue decides, not
 * facts about the document, and shipping them would invite a client to
 * re-implement that decision and disagree with it.
 *
 * `lastError` stays server-side too, and for a stronger reason (codex round 2).
 * The worker writes `Extraction failed: <raw exception message>` there — pdfjs
 * internals, a stored file path, a vision host URL — and this block goes to
 * every member who can see the attachment. What a household needs to be told is
 * that the reading failed and that it can be tried again, and `status` plus
 * `retryable` already say exactly that, in their own language. The raw text
 * stays where an operator can read it: the attachment's metadata and the
 * queue's own status endpoint.
 *
 * `retryable` is that decision, already made: true exactly when asking again
 * could produce a different answer. `unextractable` is therefore NOT retryable
 * — the document was read and holds no text, which is an answer and not a
 * fault — while `failed` and `partial` are.
 *
 * Absent from the DTO entirely for an attachment the pipeline does not read
 * (an image, a file type with no lane). Present with `queued` for one it will.
 */
export const attachmentExtractionViewSchema = z.object({
    status: attachmentExtractionViewStatusSchema,
    /** Pages whose text is stored and readable for the CURRENT file. 0 while
     * queued, by construction: a state block about older bytes is not evidence
     * about these ones. */
    pageCount: z.number().int().nonnegative(),
    /** Pages read but not yet turned into text (the OCR lane could not run).
     * Non-zero exactly on `partial`. */
    pendingPages: z.number().int().nonnegative(),
    /** Would asking again plausibly change the answer? */
    retryable: z.boolean()
});
/** Whether asking the pipeline to read this document again could change the
 * answer. One definition, so the DTO and the retry route cannot disagree about
 * which rows the button appears on. */
export function isRetryableExtractionStatus(status) {
    return status === "failed" || status === "partial";
}
/**
 * Body of `PATCH /api/v1/attachments/:id/collection-type` (#1176).
 *
 * `manual_page` is deliberately NOT accepted: those rows are one photographed
 * page inside a `collection_id`, and re-filing a single page as a `warranty`
 * would leave a manual with a hole in it that nothing puts back. Moving a page
 * out of its collection is a different operation from naming what a document
 * is, and this route is only the second one.
 */
export const updateAttachmentCollectionTypeSchema = z.object({
    collectionType: z.enum(documentCollectionTypes)
});
// ---------------------------------------------------------------------------
// Text-file version diff (#307). NOTE: keep this section at the END of the
// file — parallel work adds entries to the sections above.
// ---------------------------------------------------------------------------
/** One git-style unified hunk, as produced by jsdiff structuredPatch().
 * Shaped exactly like markdownDocumentDiffHunkSchema so the shared <TextDiff>
 * renderer consumes either response unchanged. */
export const attachmentDiffHunkSchema = z.object({
    oldStart: z.number().int().nonnegative(),
    oldLines: z.number().int().nonnegative(),
    newStart: z.number().int().nonnegative(),
    newLines: z.number().int().nonnegative(),
    lines: z.array(z.string())
});
/** Response of GET /attachments/:id/versions/:n/diff/:to. Only TEXT versions
 * diff (effective mime text/*); anything else is refused with 415. Identical
 * bytes (same content hash) short-circuit to empty hunks. */
export const attachmentVersionDiffResponseSchema = z.object({
    attachmentId: z.number().int().positive(),
    fromVersion: z.number().int().positive(),
    toVersion: z.number().int().positive(),
    hunks: z.array(attachmentDiffHunkSchema)
});
